<template>
  <div class="autocomplete w-full">
    <div
      class="autocomplete__input input"
      :class="{
        'input--error': hasError,
        'input--focus': isInputFocused,
        'input--disabled': isDisabled
      }"
    >
      <template v-if="leftIconClass">
        <div
          :class="[leftIconClass]"
          class="input__left-icon pl-16"
          @click="$emit('on-left-click')"
        ></div>
      </template>
      <div class="input__field-wrapper">
        <input
          v-model="search"
          class="input__field px-16"
          :class="{ 'pt-16': label }"
          type="text"
          :placeholder="placeholder"
          data-testid="input__field"
          :required="required"
          :disabled="isDisabled"
          @input="onInput"
          @click="onChange"
          @keydown.down="onArrowDown"
          @keydown.up="onArrowUp"
          @keydown.enter="onEnter"
          @focus="toggleInputFocus(true)"
          @blur="toggleInputFocus(false)"
        />
        <label v-if="label" class="input__label" :for="id">
          {{ label }}
        </label>
      </div>
      <template v-if="rightIconClass">
        <div
          :class="[rightIconClass]"
          class="input__right-icon pr-16"
          @click="$emit('on-right-click')"
        ></div>
      </template>
    </div>
    <ul
      v-show="isOpen"
      class="autocomplete__results"
      data-testid="autocomplete__results"
    >
      <li v-if="isLoading" class="loading">Loading results...</li>
      <li
        v-for="(result, i) in results"
        v-else
        :key="i"
        class="autocomplete__results-item"
        :class="{ 'autocomplete__results-item--active': i === arrowCounter }"
        data-testid="autocomplete__results-item"
        @click="setResult(result)"
      >
        {{ result[titlePropName] }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'SearchAutocomplete',
  props: {
    items: {
      type: Array,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    titlePropName: {
      type: String,
      required: true
    },
    required: {
      type: Boolean,
      default: true
    },
    optionValue: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    leftIconClass: {
      type: String,
      default: ''
    },
    rightIconClass: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    hasError: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    initialValue: {
      type: String,
      default: ''
    }
  },
  emits: ['selectOption', 'input', 'on-left-click', 'on-right-click'],
  data() {
    return {
      search: '',
      results: [],
      isOpen: false,
      arrowCounter: -1,
      isLoading: false,
      isInputFocused: false
    }
  },
  watch: {
    initialValue(newVal) {
      if (newVal) {
        if (this.optionValue) {
          const foundItem = this.items.find(
            (item) => item[this.optionValue] === newVal
          )
          if (foundItem && foundItem[this.titlePropName] !== undefined) {
            this.search = foundItem[this.titlePropName]
          } else {
            this.search = ''
          }
        } else {
          this.search = newVal
        }
      } else {
        this.search = ''
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  unmounted() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  updated() {
    if (this.search === '') {
      this.$emit('selectOption', this.search)
    }
  },
  methods: {
    filterResults() {
      this.results = this.items.filter(
        (item) =>
          item[this.titlePropName]
            .toLowerCase()
            .indexOf(this.search.toLowerCase()) > -1
      )
    },
    exactHasBeenTyped() {
      const exactResultTyped = this.items.find(
        (item) =>
          item[this.titlePropName].toLowerCase() === this.search.toLowerCase()
      )
      exactResultTyped && this.setResult(exactResultTyped)
    },
    onChange() {
      this.$emit('input', this.search)
      this.filterResults()
      this.isOpen = true
    },
    setResult(result) {
      this.search = result[this.titlePropName]
      this.isOpen = false
      this.$emit('selectOption', result[this.optionValue || this.titlePropName])
    },
    resetAutocomplete() {
      this.arrowCounter = -1
      this.isOpen = false
    },
    handleClickOutside(event) {
      !this.$el.contains(event.target) && this.resetAutocomplete()
    },
    onArrowDown() {
      this.arrowCounter =
        this.arrowCounter < this.results.length && this.arrowCounter + 1
    },
    onArrowUp() {
      this.arrowCounter = this.arrowCounter > 0 && this.arrowCounter - 1
    },
    onEnter() {
      this.search = this.results[this.arrowCounter][this.titlePropName]
      this.$emit('selectOption', this.search)
      this.arrowCounter = -1
      this.isOpen = false
    },
    toggleInputFocus(state) {
      this.isInputFocused = state
    },
    onInput() {
      this.onChange()
      this.exactHasBeenTyped()
    }
  }
}
</script>

<style lang="scss">
.autocomplete {
  position: relative;
  z-index: 1;

  &__input {
    position: relative;
    z-index: 2;
  }

  &--disabled {
    background-color: var(--color-gray-2);
    border-color: var(--color-brand-purple-soft) !important;
    color: var(--color-gray-3);
  }

  &__results {
    position: absolute;
    z-index: 1;
    padding: 0;
    margin: 0;
    border: 1px solid #eeeeee;
    max-height: 240px;
    overflow: auto;
    top: rem(32);
    width: 100%;
    padding: 2.2rem 1rem 1rem;
    background-color: var(--color-gray-2);
    border-radius: rem(16);
    box-shadow: 0px 12px 16px rgb(0 0 0 / 20%);

    &-item {
      margin-bottom: rem(16);
      cursor: pointer;

      &--active,
      &:hover {
        color: var(--color-brand-purple);
        font-weight: bold;
      }
    }
  }
}
</style>
